import { Link } from "react-router-dom";
import useSettingsStore from "../store/useSettingsStore";
import { Send, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const settings = useSettingsStore((s) => s.settings);
  const content = useSettingsStore((s) => s.content);

  const logoUrl = settings?.logo_url
    ? settings.logo_url.startsWith("http") ? settings.logo_url : `${BACKEND_URL}${settings.logo_url}`
    : "";
  const companyName = content?.header?.company_name || settings?.site_title || "Авто Маркет";
  const description = content?.footer?.description || "Импорт премиальных автомобилей из Европы";
  const copyright = content?.footer?.copyright || `© ${new Date().getFullYear()} Авто Маркет`;

  return (
    <footer className="border-t border-[var(--border-subtle)] mt-auto" style={{ background: "var(--surface)" }} data-testid="footer">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              {logoUrl && <img src={logoUrl} alt={companyName} className="h-10 logo-glow" />}
              <span className="text-xl font-semibold text-[var(--accent)]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {companyName}
              </span>
            </div>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{description}</p>
          </div>

          {/* Navigation */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-[var(--accent)]">Навигация</h4>
            <div className="flex flex-col gap-2">
              {[
                { to: "/catalog", label: "Каталог" },
                { to: "/configurator", label: "Подбор авто" },
                { to: "/reviews", label: "Отзывы" },
                { to: "/about", label: "О компании" },
                { to: "/contacts", label: "Контакты" },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors" data-testid={`footer-link-${l.to.slice(1)}`}>
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Telegram */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-[var(--accent)]">Мы на связи</h4>
            <div className="flex flex-col gap-3">
              {settings?.telegram_channel_url && (
                <a
                  href={settings.telegram_channel_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-premium btn-primary inline-flex items-center gap-2 !py-3 !px-6"
                  data-testid="footer-telegram-btn"
                >
                  <Send size={16} />
                  {content?.contacts?.channel_button || "Telegram-канал"}
                </a>
              )}
              {settings?.telegram_link && (
                <a
                  href={settings.telegram_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl text-sm transition-all bg-[#229ED9]/15 text-[#229ED9] hover:bg-[#229ED9]/25 border border-[#229ED9]/20"
                  data-testid="footer-tg-link"
                >
                  <Send size={14} />
                  {content?.contacts?.tg_button || "Telegram"}
                </a>
              )}
              {settings?.whatsapp_link && (
                <a
                  href={settings.whatsapp_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-2.5 px-5 rounded-xl text-sm transition-all bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25 border border-[#25D366]/20"
                  data-testid="footer-wa-link"
                >
                  <MessageCircle size={14} />
                  {content?.contacts?.wa_button || "WhatsApp"}
                </a>
              )}
            </div>
            <p className="text-sm text-[var(--text-secondary)]">
              {content?.contacts?.work_hours || "Ежедневно с 9:00 до 21:00"}
            </p>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[var(--border-subtle)] text-center">
          <p className="text-xs text-[var(--text-secondary)]">{copyright}</p>
        </div>
      </div>
    </footer>
  );
}
